import type { Command } from "./commands.ts";
import { STAND_REACH, readStandCells, standMarksIssue } from "../compile/stand-cells.ts";
import { sameJson } from "./same-json.ts";
import type { InstanceFootprint } from "./types.ts";

/**
 * Footprint cells (ADR-040): `[column, rowsUp]` collision cells counted from a frame's bottom-left, the space
 * the library's per-frame override and a placed prop's own footprint share (amendment of 2026-10-04). One
 * reader checks a cell list for both; the instance's field is bounded and held in canonical order besides.
 */

/**
 * A cell coordinate is a whole number from 0 to one under this.
 */
export const FOOTPRINT_CELL_SPAN = 1024;

/**
 * The most cells one footprint holds.
 */
export const MAX_FOOTPRINT_CELLS = 4096;

/**
 * Whether a value is one cell: a pair of whole numbers from zero.
 *
 * @param value The value.
 * @returns True for a cell.
 */
function isCell(value: unknown): value is [number, number] {
  return Array.isArray(value) && value.length === 2 && value.every((n) => typeof n === "number" && Number.isInteger(n) && n >= 0);
}

/**
 * Reads a cell list: an array of `[column, rowsUp]` pairs of whole numbers from zero, in the order given.
 *
 * @param value Candidate value.
 * @param path Path for the error message.
 * @returns The cells, copied.
 * @throws Error naming the path when the value is not a cell list.
 */
export function readFootprintCells(value: unknown, path: string): [number, number][] {
  if (!Array.isArray(value) || !value.every(isCell)) {
    throw new Error(`${path} must be [[column, rowsUp], ...]`);
  }
  return value.map((cell) => [cell[0], cell[1]]);
}

/**
 * Cells in canonical order: by rows up, then by column, each cell once, so equal footprints are equal data.
 *
 * @param cells The cells.
 * @returns The cells in order.
 */
export function canonicalCells(cells: readonly (readonly [number, number])[]): [number, number][] {
  const sorted = cells.map((cell) => [cell[0], cell[1]] as [number, number]).sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  return sorted.filter((cell, i) => i === 0 || cell[0] !== sorted[i - 1]?.[0] || cell[1] !== sorted[i - 1]?.[1]);
}

/**
 * What is wrong with cells as an instance's: too many, a coordinate out of range, or not in canonical order.
 *
 * @param cells The cells.
 * @returns The problem, or null when the cells are within the bounds and in order.
 */
function cellsProblem(cells: readonly (readonly [number, number])[]): string | null {
  if (cells.length > MAX_FOOTPRINT_CELLS) {
    return `holds at most ${MAX_FOOTPRINT_CELLS} cells, not ${cells.length}`;
  }
  if (cells.some((cell) => cell[0] >= FOOTPRINT_CELL_SPAN || cell[1] >= FOOTPRINT_CELL_SPAN)) {
    return `takes cell coordinates under ${FOOTPRINT_CELL_SPAN}`;
  }
  const ordered = cells.every((cell, i) => {
    const previous = cells[i - 1];
    return previous === undefined || cell[1] > previous[1] || (cell[1] === previous[1] && cell[0] > previous[0]);
  });
  return ordered ? null : "must list each cell once in order of rows up, then column";
}

/**
 * The names of a placed prop's own lists of mark cells, beside `cells`.
 */
const MARK_KEYS = ["behind", "front"] as const;

/**
 * Whether a value is one mark cell: a pair of whole numbers from `-STAND_REACH`.
 *
 * @param value The value.
 * @returns True for a mark cell.
 */
function isMarkCell(value: unknown): value is [number, number] {
  return Array.isArray(value) && value.length === 2 && value.every((n) => typeof n === "number" && Number.isInteger(n) && n >= -STAND_REACH);
}

/**
 * What is wrong with one of a placed prop's own mark lists: not a list of mark cells, not in canonical order (rows
 * up, then column, each once), or beyond the count and span an entry takes. An empty list is the prop's own list of
 * no cells, which says the copy has none of that mark; a list left out inherits its frame's.
 *
 * @param name The list's name, for the message.
 * @param list Candidate value.
 * @returns The problem in words that follow the footprint's name, or null.
 */
function markListProblem(name: string, list: unknown): string | null {
  if (!Array.isArray(list) || !list.every(isMarkCell)) {
    return `${name} must be [[column, rowsUp], ...] in whole numbers from -${STAND_REACH}`;
  }
  const ordered = list.every((cell, i) => {
    const previous = list[i - 1];
    return previous === undefined || cell[1] > previous[1] || (cell[1] === previous[1] && cell[0] > previous[0]);
  });
  if (!ordered) {
    return `${name} must list each cell once in order of rows up, then column`;
  }
  const limits = standMarksIssue(name === "behind" ? { behind: list } : { front: list });
  return limits === undefined ? null : limits;
}

/**
 * What is wrong with a value as a placed prop's own footprint, as a command or a live operation carries it:
 * an object holding `cells` and, optionally, `behind` and `front` (ADR-132 decision 12), each a list within the
 * bounds and in canonical order, with no cell in both `behind` and `front`; the blocked `cells` may share a cell with either, since collision and drawing are two layers (decision 14). A mark list left out
 * inherits the frame's; an empty one means none.
 *
 * @param value Candidate value.
 * @returns The problem in words that follow the field's name, or null for a footprint.
 */
export function instanceFootprintProblem(value: unknown): string | null {
  if (typeof value !== "object" || value === null || Array.isArray(value) || !Object.hasOwn(value, "cells") || Object.keys(value).some((key) => key !== "cells" && !(MARK_KEYS as readonly string[]).includes(key))) {
    return "must be an object holding cells, and optionally behind and front";
  }
  const record = value as { readonly cells: unknown; readonly behind?: unknown; readonly front?: unknown };
  if (!Array.isArray(record.cells) || !record.cells.every(isCell)) {
    return "cells must be [[column, rowsUp], ...]";
  }
  const cellsFault = cellsProblem(record.cells);
  if (cellsFault !== null) {
    return cellsFault;
  }
  for (const name of MARK_KEYS) {
    if (record[name] === undefined) {
      continue;
    }
    const fault = markListProblem(name, record[name]);
    if (fault !== null) {
      return fault;
    }
  }
  const shared = standMarksIssue({ cells: record.cells, ...(record.behind === undefined ? {} : { behind: record.behind as [number, number][] }), ...(record.front === undefined ? {} : { front: record.front as [number, number][] }) });
  return shared === undefined ? null : shared;
}

/**
 * Reads a placed prop's own footprint from a saved file, putting hand-written lists in canonical order. A mark list
 * that is present is kept, an empty one included, since it says the copy has none of that mark; one left out stays
 * out, so the copy inherits its frame's.
 *
 * @param value Candidate value.
 * @param path Path for the error message.
 * @returns The footprint.
 * @throws Error naming the path when the value is not an object of cells, holds a key other than `cells`, `behind`
 *   and `front`, a list is out of bounds, or a cell is both behind and in front.
 */
export function readInstanceFootprint(value: unknown, path: string): InstanceFootprint {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object holding cells`);
  }
  const unknown = Object.keys(value).find((key) => key !== "cells" && !(MARK_KEYS as readonly string[]).includes(key));
  if (unknown !== undefined) {
    throw new Error(`${path}.${unknown} is not a field of a prop's own footprint`);
  }
  const record = value as { readonly cells?: unknown; readonly behind?: unknown; readonly front?: unknown };
  const cells = canonicalCells(readFootprintCells(record.cells, `${path}.cells`));
  const marks: { behind?: [number, number][]; front?: [number, number][] } = {};
  for (const name of MARK_KEYS) {
    if (record[name] === undefined) {
      continue;
    }
    marks[name] = canonicalCells(readStandCells(record[name], `${path}.${name}`));
  }
  const footprint: InstanceFootprint = { cells, ...marks };
  const problem = instanceFootprintProblem(footprint);
  if (problem !== null) {
    throw new Error(`${path} ${problem}`);
  }
  return footprint;
}

/**
 * A footprint as plain data with its cells copied, for a serialiser.
 *
 * @param footprint The footprint.
 * @returns The plain object.
 */
export function plainInstanceFootprint(footprint: InstanceFootprint): { cells: [number, number][]; behind?: [number, number][]; front?: [number, number][] } {
  const copy = (list: readonly (readonly [number, number])[]): [number, number][] => list.map((cell) => [cell[0], cell[1]]);
  return { cells: copy(footprint.cells), ...(footprint.behind === undefined ? {} : { behind: copy(footprint.behind) }), ...(footprint.front === undefined ? {} : { front: copy(footprint.front) }) };
}

/**
 * Checks what a command gives a prop against the rules of a prop's own footprint: well formed, and never on a
 * prop that carries a behaviour, whose collision follows its states.
 *
 * @param prop The prop, or the state a command gives it.
 * @param propId The prop's id, for the message.
 * @throws Error naming the rule broken.
 */
export function assertInstanceFootprint(prop: { readonly footprint?: InstanceFootprint; readonly behaviour?: unknown }, propId: string): void {
  if (prop.footprint === undefined) {
    return;
  }
  const problem = instanceFootprintProblem(prop.footprint);
  if (problem !== null) {
    throw new Error(`prop ${propId} footprint ${problem}`);
  }
  if (prop.behaviour !== undefined) {
    throw new Error(`prop ${propId} carries a behaviour, so it takes no footprint of its own`);
  }
}

/**
 * How many props a command leaves without the footprint of their own they had because their frame changed or
 * they were given a behaviour (ADR-040 amendment of 2026-10-04), so the editor can say that the cells went,
 * since nothing else shows it. A clear the author asked for, with the frame as it was and no behaviour gained,
 * is not counted.
 *
 * @param command The command, a compound counted part by part.
 * @returns The count.
 */
export function ownFootprintDrops(command: Command): number {
  if (command.kind === "compound") {
    return command.commands.reduce((sum, part) => sum + ownFootprintDrops(part), 0);
  }
  if (command.kind !== "updateProp" || command.before.footprint === undefined || command.after.footprint !== undefined) {
    return 0;
  }
  const reframed = !sameJson(command.before.frame, command.after.frame);
  const behaves = command.before.behaviour === undefined && command.after.behaviour !== undefined;
  return reframed || behaves ? 1 : 0;
}
